import { z } from "zod";

import { multiSelectDefaultValuesSchema } from "./general.schema";

import { ERROR_MSG } from "@/constants/ErrorMessage.constant";
import { OptionTypeConstants } from "@/constants/constants";
import { FollowUpIntervals } from "@/constants/project-register.constant";
import {
  alphanumericValidator,
  basicValidator,
  dateValidator,
  reactSelectValidator,
  singleSelectValidator,
} from "@/lib/validators";

export type CustomerType = "CUSTOMER" | "PROSPECT";

export const ProjectRegisterSchema = z
  .object({
    projectDetails: z.discriminatedUnion(
      "type",
      [
        z.object({
          type: z.literal("CUSTOMER"),
          customerId: z.string().uuid({ message: ERROR_MSG.SELECT_CUSTOMER }),
          defaultCustomerName: z.string().optional(),
          customerUserId: reactSelectValidator(
            ERROR_MSG.SELECT_CUSTOMER_USER,
            true,
          ),
          defaultCustomerUserNames: multiSelectDefaultValuesSchema,
          prospectId: z.string().optional(),
          prospectUserId: z.any().optional(),
        }),
        z.object({
          type: z.literal("PROSPECT"),
          prospectId: z.string().uuid({ message: ERROR_MSG.SELECT_PROSPECT }),
          defaultProspectName: z.string().optional(),
          prospectUserId: reactSelectValidator(
            ERROR_MSG.SELECT_PROSPECT_USER,
            true,
          ),
          defaultProspectUserNames: multiSelectDefaultValuesSchema,
          customerId: z.string().optional(),
          customerUserId: z.any().optional(),
        }),
      ],
      {
        errorMap: (issue, ctx) => {
          if (issue.code === "invalid_union_discriminator") {
            return { message: ERROR_MSG.SELECT_CLIENT_TYPE };
          }

          return { message: ctx.defaultError };
        },
      },
    ),
    referenceNumber: alphanumericValidator({
      allowHyphen: true,
      required: true,
      allowSpace: false,
      minLength: 1,
      message: ERROR_MSG.REQUIRED,
    }),

    projectNumber: alphanumericValidator({
      allowHyphen: false,
      required: false,
      allowSpace: false,
      message: ERROR_MSG.REQUIRED,
    }),
    projectName: basicValidator(true, "Required", 1),
    dueDate: dateValidator(true, ERROR_MSG.DUE_DATE_REQ),
    receivedDate: dateValidator(true, ERROR_MSG.DATE_REQ),
    address: z.object({
      longitude: z.string().min(3),
      latitude: z.string().min(3),
      label: z.string().min(1),
      areaLocation: z.string().optional(),
    }),
    buildersNo: z.coerce
      .number({
        invalid_type_error: ERROR_MSG.INVALID_BUILDER_NO,
      })
      .max(9999999)
      .optional(),
    ourTeam: reactSelectValidator(ERROR_MSG.SELECT_TEAM, true),
    ourTeamDefaultValue: multiSelectDefaultValuesSchema,
    constructionType: z
      .string()
      .uuid({ message: ERROR_MSG.SELECT_CONSTRUCTION_TYPE })
      .optional(),
    dealDescription: z.array(z.string()).optional(),
    estimationType: singleSelectValidator(false),
    source: singleSelectValidator(false),
    inspectors: reactSelectValidator(ERROR_MSG.SELECT_INSPECTORS, true),
    inspectorsDefaultValue: multiSelectDefaultValuesSchema,
    inspectionDate: dateValidator(),
    siteInspection: singleSelectValidator(false),
    status: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (!data?.referenceNumber) {
      return;
    }
    //Split the reference number prefix and suffix
    const [, suffix] = data.referenceNumber.split("-");
    if (!suffix) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: ERROR_MSG.REQUIRED,
        path: ["referenceNumber"],
      });
    }
  });

export type TProjectRegister = z.infer<typeof ProjectRegisterSchema>;

const ItemDetailSchema = z.object({
  id: z.string().trim().optional(),
  item: basicValidator(true, ERROR_MSG.ITEM_NAME_REQ, 1),
  description: z
    .string({ message: ERROR_MSG.DESCRIPTION_REQ })
    .trim()
    .optional(),
  position: z.coerce.number().min(0),
  unit: z.string({ message: ERROR_MSG.UNIT_REQ }).trim().min(1, {
    message: ERROR_MSG.UNIT_REQ,
  }),
  quantity: z.coerce
    .number({ message: ERROR_MSG.QUANTITY_REQ })
    .superRefine((data, ctx) => {
      if (data < 0 || data === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Quantity is required",
        });
      }
    }),
  price: z.coerce
    .number({
      required_error: ERROR_MSG.PRICE_REQ,
    })
    .refine((value) => value !== 0, {
      message: ERROR_MSG.REQUIRED,
    }),
  margin: z.coerce.number().min(0),
  total: z.coerce
    .number({ message: ERROR_MSG.TOTAL_REQ })
    .min(0, { message: "Total cannot be 0" }),
});

const CreateEstimationItemSchema = ItemDetailSchema.and(
  z.union([
    z.object({
      isCustom: z.literal(true),
      customDetails: z.array(ItemDetailSchema),
    }),
    z.object({
      isCustom: z.literal(false),
    }),
  ]),
);

const EstimationSectionSchema = z.object({
  name: basicValidator(true, "Section Name is Required", 1),
  total: z.coerce
    .number({ message: "Total cannot be empty" })
    .min(1, { message: "Total cannot be 0" }),
  note: z.string().optional(),
  position: z.coerce.number().min(0),
  details: z.array(CreateEstimationItemSchema),
});

export const MainEstimationSchema = z.object({
  note: z.string(),
  mainCost: z.array(EstimationSectionSchema),
  optionalCost: z.array(EstimationSectionSchema).optional(),
});

export type TEstimationSectionSchema = z.infer<typeof EstimationSectionSchema>;
export type TEstimationMainCostSchema = z.infer<typeof MainEstimationSchema>;
export type TMainEstimation = z.infer<typeof MainEstimationSchema>;

export const forOptions = z.union([
  z.literal(OptionTypeConstants.ESTIMATION_STATUS),
  z.literal(OptionTypeConstants.ESTIMATION_SUBMISSION_METHOD),
  z.literal(OptionTypeConstants.PO_STATUS),
  z.literal(OptionTypeConstants.CLAIM_STATUS),
  z.literal(OptionTypeConstants.INVOICE_METHOD),
  z.literal(OptionTypeConstants["Task & Claims"]),
  z.literal(OptionTypeConstants.PROJECT_REGISTER_STATUS),
  z.literal(OptionTypeConstants.CONSTRUCTION_TYPE),
  z.literal(OptionTypeConstants.ESTIMATION_TYPE),
  z.literal(OptionTypeConstants.SOURCE),
  z.literal(OptionTypeConstants.INSPECTION),
  z.literal(OptionTypeConstants.DEAL_DESCRIPTION),
  z.literal(OptionTypeConstants.PROJECT_REGISTER_SUBMISSION_STATUS),
]);

export type TForOptions = z.infer<typeof forOptions>;

export const EOption = z.object({
  id: z.string().optional(),
  name: basicValidator(true, "Name is Required", 1),
  for: forOptions,
  // for: z.enum(["ESTIMATION_STATUS", "ESTIMATION_SUBMISSION_METHOD"]),
  details: z.object({
    position: z.coerce.number({ message: "Position is Required." }),
    textColor: z
      .string({ message: "Text Color is Required." })
      .min(3, { message: "Text Color is Required." }),
    textOpacity: z.coerce
      .number({ message: "Text Opacity is Required." })
      .min(0)
      .max(100),
    backgroundColor: z
      .string({ message: "Background Color is Required." })
      .min(3, {
        message: "Background Color is Required.",
      }),
    backgroundOpacity: z.coerce
      .number({ message: "Background Opacity is required." })
      .min(0)
      .max(100),
  }),
  workspaceId: z.string().uuid().optional(),
});

export const EstimateOption = z.object({
  data: z.array(EOption),
});

export const claimsOptionSchema = EOption.extend({
  for: z.union([
    z.literal("PO_STATUS"),
    z.literal("CLAIM_STATUS"),
    z.literal("INVOICE_METHOD"),
  ]),
});

export type TClaimsOption = z.infer<typeof claimsOptionSchema>;

export type TEOption = z.infer<typeof EOption>;
export type TEstimateOption = z.infer<typeof EstimateOption>;

export const FollowUpSchema = z.discriminatedUnion("isFollowUpRequired", [
  z.object({
    isFollowUpRequired: z.literal(false),
  }),
  z.object({
    isFollowUpRequired: z.literal(true),
    followUpDetails: z.object({
      id: z.string().optional(),
      teamMembers: reactSelectValidator().superRefine((data, ctx) => {
        if (!data) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Select at least one team member.",
          });
        }
      }),
      intervals: z.nativeEnum(FollowUpIntervals, {
        message: "Follow Up Interval is Required",
      }),
      startingDate: dateValidator(true, "Starting Date is Required"),
    }),
  }),
]);

export const EstimationBaseSchema = z.object({
  quoteVersion: z.string().optional(),
  quoteFile: z.string().optional(),
  amount: z.coerce.number(),
  status: basicValidator(true, "Status is Required"),
  submittedDate: dateValidator(true, "Date is Required"),
  submissionMethod: basicValidator(true, "Status is Required"),
  estimationTable: MainEstimationSchema.optional(),
});

export const UpdateEstimationSchema = EstimationBaseSchema.and(FollowUpSchema);

export type TFollowUpSchema = z.infer<typeof FollowUpSchema>;
export type TUpdateEstimationSchema = z.infer<typeof UpdateEstimationSchema>;
export type TEstimationBaseSchema = z.infer<typeof EstimationBaseSchema>;

export const estimatesReviewSchema = z.object({
  review: basicValidator(),
});

export type TEstimatesReviewSchema = z.infer<typeof estimatesReviewSchema>;

export const userIdsSchema = z.object({
  Clients: reactSelectValidator("Select at least one user", true),
});

export type TUserIdsSchema = z.infer<typeof userIdsSchema>;
